/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/**
 * Style generating functions
 */
/* eslint-disable no-duplicate-imports */
import { IAvatarStyling, IBubbleStyling, IChatCustomStyle, IChatStyling, IOtherStyling, ISuggestedActionStyling } from '../interfaces';
import { IPvaChatConnectorConfig } from '../pva-chat-connector.props.autogenerated';

const getChatWrapperStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    return <React.CSSProperties>{
        position: config.position || 'inherit',
        top: config.top ? config.top : 'inherit',
        right: config.right ? config.right : 'inherit',
        bottom: config.bottom ? config.bottom : 'inherit',
        left: config.left ? config.left : 'inherit',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'flex-end'
    };
};

const getMaximizedChatWrapperStyle = (config: IPvaChatConnectorConfig, isMobileDevice: boolean): React.CSSProperties => {
    if (isMobileDevice) {
        return <React.CSSProperties>{
            height: '90%',
            width: '100%',
            bottom: '0.5em',
            right: '0',
            padding: '5px'
        };
    }
    return <React.CSSProperties>{
        height: config.chatHeight ? config.chatHeight : '80%',
        width: config.chatWidth ? config.chatWidth : 'inherit'
    };
};

const getChatButtonStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    return <React.CSSProperties>{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: config.startChatButtonBackgroundColor || 'white',
        color: config.startChatButtonTextColor || 'inherit',
        borderColor: config.startChatButtonBorderColor || 'inherit',
        borderRadius: config.startChatButtonBorderRadius || '0px',
        borderStyle: config.startChatButtonBorderStyle || 'solid',
        borderWidth: config.startChatButtonBorderWidth ? config.startChatButtonBorderWidth : '0px',
        padding: config.storeLogoURL ? '2px 20px 2px 2px' : '10px 20px 10px 20px',
        cursor: 'pointer',
        outlineStyle: 'none',
        alignSelf: 'flex-end',
        boxShadow: '0 0 4px rgba(102,102,102,.5)',
        WebkitBoxShadow: '0 0 4px rgba(102,102,102,.5)',
        MozBoxShadow: '0 0 4px rgba(102,102,102,.5)'
    };
};

const getButtonStoreLogoStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    return <React.CSSProperties>{
        backgroundColor: config.headerBackgroundColor || 'inherit',
        color: config.headerTextColor || 'inherit',
        borderRadius: '50%',
        padding: '11px',
        width: '30px',
        height: '30px',
        marginRight: '12px',
        borderStyle: 'solid',
        borderWidth: '1px',
        boxSizing: 'content-box'
    };
};

const getChatStoreLogoStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    return <React.CSSProperties>{
        backgroundColor: config.headerBackgroundColor || 'inherit',
        color: config.headerTextColor || 'inherit',
        borderRadius: '50%',
        padding: '11px',
        width: '30px',
        height: '30px',
        margin: '5px 12px 5px 5px',
        alignSelf: 'center',
        borderStyle: 'solid',
        borderWidth: '1px',
        boxSizing: 'content-box'
    };
};

const getChatMessagesStyle = (config: IPvaChatConnectorConfig, minimized: boolean): React.CSSProperties => {
    return <React.CSSProperties>{
        display: minimized ? 'none' : 'block',
        backgroundColor: config.backgroundColor || 'White',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative'
    };
};

const getChatMessageHeader = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    config.chatHeaderHeight = config.chatHeaderHeight || '70px';
    return <React.CSSProperties>{
        backgroundColor: config.headerBackgroundColor || 'inherit',
        backgroundImage: `url("${config.headerBackgroundImageURL}")` || 'none',
        color: config.headerTextColor || 'inherit',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'row',
        width: '100%',
        position: 'absolute',
        top: config.chatHeaderHeight ? `-${config.chatHeaderHeight}` : '',
        height: config.chatHeaderHeight,
        borderColor: config.headerBackgroundColor ? config.headerBackgroundColor : 'inherit',
        borderStyle: config.chatBorderStyle ? config.chatBorderStyle : 'none',
        borderWidth: config.chatBorderWidth ? config.chatBorderWidth : '0px'
    };
};

const getMinimizeButtonStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    config.chatHeaderHeight = config.chatHeaderHeight || '70px';
    return <React.CSSProperties>{
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: `solid 2px ${config.headerTextColor || 'White'}`,
        backgroundColor: 'transparent',
        padding: '5px 10px 3px 10px',
        cursor: 'pointer',
        outlineStyle: 'none',
        marginTop: '7px',
        marginRight: '7px'
    };
};

const getCloseButtonStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    config.chatHeaderHeight = config.chatHeaderHeight || '70px';
    return <React.CSSProperties>{
        border: 'none',
        color: config.headerTextColor || 'White',
        backgroundColor: 'transparent',
        padding: '0px 0px 0px 7px',
        cursor: 'pointer',
        outlineStyle: 'none',
        marginTop: '7px',
        marginRight: '7px'
    };
};

const getChatMessageContent = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    config.chatHeaderHeight = config.chatHeaderHeight || '70px';
    return <React.CSSProperties>{
        height: '100%',
        width: '100%',
        borderColor: config.chatBorderColor ? config.chatBorderColor : 'inherit',
        borderStyle: config.chatBorderStyle ? config.chatBorderStyle : 'none',
        borderWidth: config.chatBorderWidth ? config.chatBorderWidth : '0px'
    };
};

const getProactiveWrapperStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    return <React.CSSProperties>{
        flexDirection: 'column',
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: config.backgroundColor || 'White'
    };
};

const getProactiveMessageHeader = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    config.chatHeaderHeight = config.chatHeaderHeight || '70px';
    return <React.CSSProperties>{
        backgroundColor: config.headerBackgroundColor || 'inherit',
        backgroundImage: `url("${config.headerBackgroundImageURL}")` || 'none',
        color: config.headerTextColor || 'inherit',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        height: config.chatHeaderHeight,
        borderColor: config.headerBackgroundColor ? config.headerBackgroundColor : 'inherit',
        borderStyle: config.chatBorderStyle ? config.chatBorderStyle : 'none',
        borderWidth: config.chatBorderWidth ? config.chatBorderWidth : '0px'
    };
};

const getProactiveMessageContent = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    config.chatHeaderHeight = config.chatHeaderHeight || '70px';
    return <React.CSSProperties>{
        width: '100%',
        padding: '10px 20px',
        boxSizing: 'border-box',
        borderColor: config.chatBorderColor ? config.chatBorderColor : 'inherit',
        borderStyle: config.chatBorderStyle ? config.chatBorderStyle : 'none',
        borderWidth: config.chatBorderWidth ? config.chatBorderWidth : '0px'
    };
};

const getProactiveButtonStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    config.chatHeaderHeight = config.chatHeaderHeight || '70px';
    return <React.CSSProperties>{
        color: config.headerTextColor || 'inherit',
        cursor: 'pointer',
        padding: '5px 10px',
        outline: 'none',
        border: 'none',
        backgroundColor: config.headerBackgroundColor || 'inherit',
        borderRadius: '25px'
    };
};

const getRedDotStyle = (config: IPvaChatConnectorConfig): React.CSSProperties => {
    return <React.CSSProperties>{
        background: 'Red',
        borderRadius: '50%',
        height: '5px',
        width: '5px',
        position: 'absolute',
        right: '10px',
        bottom: '33px'
    };
};

/**
 * Generates styles for the chat wrapper, and proactive chat parts
 * @param config IPvaChatConnectorConfig
 * @param minimized boolean
 * @returns IChatCustomStyle
 */
export const getChatStyling = (config: IPvaChatConnectorConfig, minimized: boolean, isMobileDevice: boolean): IChatCustomStyle => {
    return <IChatCustomStyle>{
        ChatWrapperStyle: getChatWrapperStyle(config),
        MaximizedChatWrapperStyle: getMaximizedChatWrapperStyle(config, isMobileDevice),
        ChatButtonStyle: getChatButtonStyle(config),
        ButtonStoreLogoStyle: getButtonStoreLogoStyle(config),
        ChatStoreLogoStyle: getChatStoreLogoStyle(config),
        ChatMessagesStyle: getChatMessagesStyle(config, minimized),
        ChatMessageHeader: getChatMessageHeader(config),
        MinimizeButtonStyle: getMinimizeButtonStyle(config),
        CloseButtonStyle: getCloseButtonStyle(config),
        ChatMessageContent: getChatMessageContent(config),
        ProactiveWrapperStyle: getProactiveWrapperStyle(config),
        ProactiveMessageHeader: getProactiveMessageHeader(config),
        ProactiveMessageContent: getProactiveMessageContent(config),
        ProactiveButtonStyle: getProactiveButtonStyle(config),
        RedDotStyle: getRedDotStyle(config)
    };
};

const fontFormat = (font?: string): string | undefined => {
    if (font && font.length > 0) {
        const arr = font.split(',');
        for (let i = 0; i < arr.length; i++) {
            arr[i] = `'${arr[i].trim()}'`;
        }
        font = arr.join(', ');
    } else {
        font = "'Calibri', 'Helvetica Neue', 'Arial', 'sans-serif'";
    }

    return font;
};

const getBubbleStyling = (config: IPvaChatConnectorConfig): IBubbleStyling => {
    return {
        bubbleBackground: config.bubbleBackground || 'White',
        bubbleTextColor: config.bubbleTextColor || 'Black',
        bubbleBorderColor: config.bubbleBorderColor || '#e6e6e6',
        bubbleBorderRadius: config.bubbleBorderRadius || '2',
        bubbleBorderStyle: config.bubbleBorderStyle || 'solid',
        bubbleBorderWidth: config.bubbleBorderWidth || '1',
        bubbleFromUserBackground: config.bubbleFromUserBackground || 'White',
        bubbleFromUserTextColor: config.bubbleFromUserTextColor || 'Black',
        bubbleFromUserBorderColor: config.bubbleFromUserBorderColor || '#e6e6e6',
        bubbleFromUserBorderRadius: config.bubbleFromUserBorderRadius || '2',
        bubbleFromUserBorderStyle: config.bubbleFromUserBorderStyle || 'solid',
        bubbleFromUserBorderWidth: config.bubbleFromUserBorderWidth || '1'
    };
};

// eslint-disable-next-line complexity
const getOtherStyling = (config: IPvaChatConnectorConfig): IOtherStyling => {
    return {
        groupTimestamp: config.groupTimestamp || true,
        sendTimeout: config.sendTimeout || '20000',
        sendTimeoutForAttachments: config.sendTimeoutForAttachments || '120000',
        timestampColor: config.timestampColor || 'undefined',
        timestampFormat: config.timestampFormat || 'undefined',
        notificationDebounceTimeout: config.notificationDebounceTimeout || '400',
        hideToaster: config.hideToaster || true,
        toasterHeight: config.toasterHeight || '32',
        toasterMaxHeight: config.toasterMaxHeight || '32 * 5',
        toasterSingularMaxHeight: config.toasterSingularMaxHeight || '50',
        toastFontSize: config.toastFontSize || '87.5%',
        toastIconWidth: config.toastIconWidth || '36',
        toastSeparatorColor: config.toastSeparatorColor || '#E8EAEC',
        toastTextPadding: config.toastTextPadding || '6',
        toastErrorBackgroundColor: config.toastErrorBackgroundColor || '#FDE7E9',
        toastErrorColor: config.toastErrorColor || '#A80000',
        toastInfoBackgroundColor: config.toastInfoBackgroundColor || '#CEF1FF',
        toastInfoColor: config.toastInfoColor || '#105E7D',
        toastSuccessBackgroundColor: config.toastSuccessBackgroundColor || '#DFF6DD',
        toastSuccessColor: config.toastSuccessColor || '#107C10',
        toastWarnBackgroundColor: config.toastWarnBackgroundColor || '#FFF4CE',
        toastWarnColor: config.toastWarnColor || '#3B3A39',
        typingAnimationBackgroundImage: config.typingAnimationBackgroundImage || 'undefined',
        typingAnimationDuration: config.typingAnimationDuration || '5000',
        typingAnimationHeight: config.typingAnimationHeight || '20',
        typingAnimationWidth: config.typingAnimationWidth || '64',
        videoHeight: config.videoHeight || '270',
        markdownExternalLinkIconImage:
            config.markdownExternalLinkIconImage ||
            'url(data:image/svg+xml;base64,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)',
        markdownRespectCRLF: config.markdownRespectCRLF || 'true',
        internalLiveRegionFadeAfter: config.internalLiveRegionFadeAfter || '1000',
        accent: config.accent || '#0063B1',
        subtle: config.subtle || '#767676'
    };
};

const getAvatarStyling = (config: IPvaChatConnectorConfig): IAvatarStyling => {
    return {
        avatarBorderRadius: config.avatarBorderRadius || '50%',
        avatarSize: config.avatarSize || '40',
        botAvatarBackgroundColor: config.botAvatarBackgroundColor || 'undefined',
        botAvatarImage: config.botAvatarImage || '',
        botAvatarInitials: config.botAvatarInitials || '',
        userAvatarBackgroundColor: config.userAvatarBackgroundColor || 'undefined',
        userAvatarImage: config.userAvatarImage || '',
        userAvatarInitials: config.userAvatarInitials || '',
        showAvatarInGroup: config.showAvatarInGroup || 'status'
    };
};

const getSuggestedActionStyling = (config: IPvaChatConnectorConfig): ISuggestedActionStyling => {
    return {
        suggestedActionLayout: config.suggestedActionLayout || 'carousel',
        suggestedActionBackground: config.suggestedActionBackground || 'White',
        suggestedActionTextColor: config.suggestedActionTextColor || '',
        suggestedActionBorderColor: config.suggestedActionBorderColor || '',
        suggestedActionBorderRadius: config.suggestedActionBorderRadius || '0',
        suggestedActionBorderStyle: config.suggestedActionBorderStyle || 'solid',
        suggestedActionBorderWidth: config.suggestedActionBorderWidth || '2',
        suggestedActionDisabledBorderStyle: config.suggestedActionDisabledBorderStyle || 'solid',
        suggestedActionDisabledBorderWidth: config.suggestedActionDisabledBorderWidth || '2',
        suggestedActionDisabledTextColor: config.suggestedActionDisabledTextColor || '',
        suggestedActionHeight: config.suggestedActionHeight || '40',
        suggestedActionImageHeight: config.suggestedActionImageHeight || '20',
        suggestedActionsCarouselFlipperCursor: config.suggestedActionsCarouselFlipperCursor || '',
        suggestedActionsCarouselFlipperBoxWidth: config.suggestedActionsCarouselFlipperBoxWidth || '40',
        suggestedActionsCarouselFlipperSize: config.suggestedActionsCarouselFlipperSize || '20',
        suggestedActionsFlowMaxHeight: config.suggestedActionsFlowMaxHeight || '',
        suggestedActionsStackedHeight: config.suggestedActionsStackedHeight || '',
        suggestedActionsStackedOverflow: config.suggestedActionsStackedOverflow || '2',
        suggestedActionsStackedLayoutButtonMaxHeight: config.suggestedActionsStackedLayoutButtonMaxHeight || '',
        suggestedActionsStackedLayoutButtonTextWrap: config.suggestedActionsStackedLayoutButtonTextWrap || false
    };
};

/**
 * Generates style options for the Botframework-WebChat widget
 * @param config IPvaChatConnectorConfig
 * @returns IChatStyling
 */
export const getChatStyleOptions = (config: IPvaChatConnectorConfig): IChatStyling => {
    const bubbleStyling = getBubbleStyling(config);
    const avatarStyling = getAvatarStyling(config);
    const suggestedActionStyling = getSuggestedActionStyling(config);
    const otherStyling = getOtherStyling(config);
    return {
        hideUploadButton: true,
        primaryFont: fontFormat(config.primaryFont),
        backgroundColor: config.backgroundColor || 'White',
        ...bubbleStyling,
        ...avatarStyling,
        ...suggestedActionStyling,
        ...otherStyling
    };
};
